import type { AlertProps, SxProps } from "@mui/material";
import Alert from "@mui/material/Alert";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import type { ReactNode, FC } from "react";
import { useMemo } from "react";

const defaultCheckboxLabel: ReactNode = "I acknowledge this and understand the implications";

export interface ActivationStatusBannerProps {
  title: ReactNode;
  body: ReactNode;
  date?: Date;
  isDeactivation?: boolean;
  isExpired?: boolean;
  isWarningAcknowledged?: boolean;
  setIsWarningAcknowledged?: (value: boolean) => void;
  checkboxLabel?: ReactNode;
  sx?: SxProps;
}

const defaultSx: SxProps = { marginBottom: "2rem" };

export const ActivationStatusBanner: FC<ActivationStatusBannerProps> = ({
  title,
  body,
  date,
  isDeactivation,
  isExpired,
  isWarningAcknowledged,
  setIsWarningAcknowledged,
  checkboxLabel = defaultCheckboxLabel,
  sx = defaultSx,
}) => {
  const showCheckbox = isWarningAcknowledged !== undefined && setIsWarningAcknowledged !== undefined;

  const alertColor: AlertProps["color"] = useMemo(() => {
    if (isDeactivation !== undefined) {
      return isDeactivation ? "error" : "warning";
    }

    if (isExpired) return "muted";

    return undefined;
  }, [isDeactivation, isExpired]);

  return (
    <Alert severity="warning" color={alertColor} sx={sx}>
      <p>
        <b>{title}</b>
      </p>
      {date && (
        <p style={{ marginBottom: "0.5rem" }}>
          <i>Since {date.toLocaleDateString("en-us", { dateStyle: "medium" })}</i>
        </p>
      )}
      <p>{body}</p>
      {showCheckbox && (
        <FormControlLabel
          control={
            <Checkbox checked={isWarningAcknowledged} onChange={(_, checked) => setIsWarningAcknowledged(checked)} />
          }
          label={checkboxLabel}
        />
      )}
    </Alert>
  );
};
